import React, { useState, useEffect } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Image,
  Typography,
  Divider,
  Modal,
  Form,
  Input,
  message,
  Spin
} from 'antd'
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import { PHOTO_URL } from '../../utils/apiutils'
import dayjs from 'dayjs'
import Hint from '../../components/common/Hint'
import { formatTz } from '../../utils/timezone'

const { Title, Text } = Typography
const { TextArea } = Input

const PageContainer = styled.div`
  padding: 0;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;
  }
`

const StatusTag = ({ status }) => {
  const config = {
    pending: { color: 'gold', text: '待審核' },
    approved: { color: 'green', text: '已批准' },
    rejected: { color: 'red', text: '已拒絕' }
  }
  const { color, text } = config[status] || { color: 'default', text: status }
  return <Tag color={color}>{text}</Tag>
}

const PaymentApplicationDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const [reviewLoading, setReviewLoading] = useState(false)
  const [rejectModalVisible, setRejectModalVisible] = useState(false)
  const [reviewForm] = Form.useForm()

  const [configModalVisible, setConfigModalVisible] = useState(false)
  const [configLoading, setConfigLoading] = useState(false)
  const [configForm] = Form.useForm()

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/api/admin/payment-applications/${id}`)
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (error) {
      console.error('獲取詳情失敗:', error)
      message.error('獲取詳情失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchData()
    }
  }, [id])

  const handleReview = async (status, notes = '') => {
    if (!id) {
      message.error('錯誤：找不到申請 ID')
      return
    }
    setReviewLoading(true)
    try {
      console.log('Submitting review:', { id, status, notes })
      await api.put(`/api/admin/payment-applications/${id}/review`, {
        status,
        reviewNotes: notes
      })
      message.success(`申請已${status === 'approved' ? '批准' : '拒絕'}`)
      setRejectModalVisible(false)
      reviewForm.resetFields()
      fetchData()

      if (status === 'approved') {
        Modal.confirm({
          title: '申請已批准',
          content: '是否現在配置店家的支付金鑰信息？',
          okText: '立即配置',
          cancelText: '稍後',
          onOk: () => setConfigModalVisible(true)
        })
      }
    } catch (error) {
      console.error('審核操作失敗:', error)
      const errorMsg =
        error.response?.data?.message || error.message || '未知錯誤'
      message.error(`審核操作失敗: ${errorMsg}`)
    } finally {
      setReviewLoading(false)
    }
  }

  const handleConfigSubmit = async (values) => {
    setConfigLoading(true)
    try {
      await api.post(
        `/api/admin/payment-applications/${id}/payment-config`,
        values
      )
      message.success('支付配置設置成功')
      setConfigModalVisible(false)
      configForm.resetFields()
      fetchData()
    } catch (error) {
      message.error('設置支付配置失敗')
    } finally {
      setConfigLoading(false)
    }
  }

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  if (!data) {
    return (
      <PageContainer>
        <Hint
          title='找不到申請數據'
          tone='error'
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Header>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/payment-applications')}
          >
            返回列表
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            申請詳情
          </Title>
        </Space>

        <Space>
          {data.status === 'pending' && (
            <>
              <Button danger onClick={() => setRejectModalVisible(true)}>
                拒絕申請
              </Button>
              <Button
                type='primary'
                loading={reviewLoading}
                onClick={() => handleReview('approved')}
              >
                批准申請
              </Button>
            </>
          )}
          {data.status === 'approved' && (
            <Button
              type='primary'
              icon={<SettingOutlined />}
              onClick={() => setConfigModalVisible(true)}
            >
              配置支付金鑰
            </Button>
          )}
        </Space>
      </Header>

      {data.hasPaymentConfig && (
        <Hint
          title='已配置支付金鑰信息'
          tone='success'
          style={{ marginBottom: 24 }}
        />
      )}

      {data.status === 'rejected' && data.reviewNotes && (
        <Hint
          title='申請已拒絕'
          tone='error'
          style={{ marginBottom: 24 }}
        >
          {`拒絕原因：${data.reviewNotes}`}
        </Hint>
      )}

      <StyledCard title='基本資訊'>
        <Descriptions
          bordered
          column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label='申請編號'>
            {data.applicationId}
          </Descriptions.Item>
          <Descriptions.Item label='提交時間'>
            {formatTz(data.createdAt, 'YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label='當前狀態'>
            <StatusTag status={data.status} />
          </Descriptions.Item>

          <Descriptions.Item label='店家名稱'>
            {data.businessId?.name || data.businessName}
          </Descriptions.Item>
          <Descriptions.Item label='店家帳號'>
            {data.businessId?.username || data.username}
          </Descriptions.Item>
          <Descriptions.Item label='店家類型'>
            {data.storeType === 'individual' ? '個人' : '公司'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <StyledCard title='申請人資訊'>
        <Descriptions
          bordered
          column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label='中文姓名'>
            {data.memberName}
          </Descriptions.Item>
          <Descriptions.Item label='英文姓名'>
            {data.memberEnglishName}
          </Descriptions.Item>
          <Descriptions.Item label='出生日期'>
            {dayjs(data.birthDate).format('YYYY-MM-DD')}
          </Descriptions.Item>

          <Descriptions.Item label='身分證字號/統編'>
            {data.adminAccount}
          </Descriptions.Item>
          <Descriptions.Item label='聯繫電話'>
            {data.adminPhone}
          </Descriptions.Item>
          <Descriptions.Item label='電子郵箱'>
            {data.adminEmail}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <StyledCard title='銀行帳戶資訊'>
        <Descriptions
          bordered
          column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label='帳戶類別'>
            {data.bankCategory === 'bank' ? '銀行' : '郵局'}
          </Descriptions.Item>
          <Descriptions.Item label='銀行名稱'>
            {data.bankName}
          </Descriptions.Item>
          <Descriptions.Item label='分行名稱'>
            {data.branchName}
          </Descriptions.Item>
          <Descriptions.Item label='戶名'>{data.accountName}</Descriptions.Item>
          <Descriptions.Item label='帳號' span={2}>
            {data.accountNumber}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <StyledCard title='申請文件'>
        {data.documents ? (
          <Space size='large' wrap align='start'>
            {Object.entries(data.documents).map(([key, url]) => {
              if (!url) return null
              let label = ''
              switch (key) {
                case 'bankbookImage':
                  label = '存摺封面'
                  break
                case 'idCardImage':
                  label = '身分證正面'
                  break // 注意 JSON 中是 idCardImage 而不是 idCardFront
                case 'idCardFront':
                  label = '身分證正面'
                  break
                case 'idCardBack':
                  label = '身分證背面'
                  break
                case 'healthCardImage':
                  label = '健保卡正面'
                  break
                default:
                  label = key
              }

              // 加上前綴 PHOTO_URL，如果 url 已經包含 http 则不加
              const imageUrl = url.startsWith('http')
                ? url
                : `${PHOTO_URL}${url}`

              return (
                <div key={key} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '8px',
                      padding: '8px',
                      background: '#fafafa'
                    }}
                  >
                    <Image
                      width={300}
                      src={imageUrl}
                      placeholder={
                        <Image preview={false} src={imageUrl} width={300} />
                      }
                      style={{ objectFit: 'contain', maxHeight: '200px' }}
                    />
                  </div>
                  <div style={{ marginTop: 8, fontWeight: 500 }}>{label}</div>
                </div>
              )
            })}
          </Space>
        ) : (
          <Text type='secondary'>無上傳文件</Text>
        )}
      </StyledCard>

      {/* 拒絕原因 Modal */}
      <Modal
        title='拒絕申請'
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={() => reviewForm.submit()}
        confirmLoading={reviewLoading}
        okButtonProps={{ danger: true }}
      >
        <Form
          form={reviewForm}
          layout='vertical'
          onFinish={(values) => handleReview('rejected', values.reviewNotes)}
        >
          <Form.Item
            name='reviewNotes'
            label='拒絕原因'
            rules={[{ required: true, message: '請填寫拒絕原因' }]}
          >
            <TextArea rows={4} placeholder='請說明拒絕原因，以便店家修改...' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 支付配置 Modal */}
      <Modal
        title='配置支付金鑰'
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={() => configForm.submit()}
        confirmLoading={configLoading}
      >
        <Hint
          title='請填寫從支付機構獲取的配置信息'
          tone='info'
          style={{ marginBottom: 24 }}
        >
          這些信息將被加密存儲。MerchantID 為商家代號，HashKey 和 HashIV 用於加密交易數據。
        </Hint>
        <Form form={configForm} layout='vertical' onFinish={handleConfigSubmit}>
          <Form.Item
            name='MerchantID'
            label='商家代號 (MerchantID)'
            rules={[{ required: true, message: '請輸入商家代號' }]}
          >
            <Input placeholder='例如：3002607' />
          </Form.Item>
          <Form.Item
            name='HashKey'
            label='HashKey'
            rules={[
              { required: true, message: '請輸入 HashKey' },
              { len: 32, message: 'HashKey 必須是 32 個字符' }
            ]}
          >
            <Input.Password placeholder='輸入 32 位 HashKey' />
          </Form.Item>
          <Form.Item
            name='HashIV'
            label='HashIV'
            rules={[
              { required: true, message: '請輸入 HashIV' },
              { len: 16, message: 'HashIV 必須是 16 個字符' }
            ]}
          >
            <Input.Password placeholder='輸入 16 位 HashIV' />
          </Form.Item>
          <Divider />
          <Form.Item name='returnURL' label='支付返回 URL (可選)'>
            <Input placeholder='https://...' />
          </Form.Item>
          <Form.Item name='notifyURL' label='支付通知 URL (可選)'>
            <Input placeholder='https://...' />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  )
}

export default PaymentApplicationDetail
